<template>
   <div class="flex flex-col gap-1" :class="{ 'w-full': grow }">
      <div class="flex items-end justify-between">
         <Label v-if="label" :disabled="disabled">{{ label }}</Label>
         <div class="flex">
            <div ref="selectorRef" class="bg-light-grey flex items-center gap-1 rounded-[6px] p-1">
               <div
                  v-for="lang in locales"
                  class="__clickable h-fit rounded-[6px] px-1.5 py-0.5"
                  :class="{
                     'bg-secondary text-primary': model?.[lang],
                     '!bg-primary !text-secondary': selectedLocale === lang,
                  }"
                  @click="selectedLocale = lang"
               >
                  <p class="p-s">
                     {{ lang.toUpperCase() }}
                  </p>
               </div>
            </div>
         </div>
      </div>
      <TextInput
         v-if="mode === 'text'"
         grow
         :error="error"
         :disabled="disabled"
         :maxLength="maxLength"
         :model-value="model?.[selectedLocale]"
         @update:model-value="(e) => updateLocale(selectedLocale, e)"
      />
      <TextArea
         v-else-if="mode === 'textarea'"
         grow
         :error="error"
         :disabled="disabled"
         :maxLength="maxLength"
         :model-value="model?.[selectedLocale] || ''"
         @update:model-value="(e) => updateLocale(selectedLocale, e)"
      />
      <RichTextEditor
         v-else
         grow
         :error="error"
         :disabled="disabled"
         :maxLength="maxLength"
         :model-value="model?.[selectedLocale] || ''"
         @update:model-value="(e) => updateLocale(selectedLocale, e)"
      />
   </div>
</template>

<script lang="ts" setup>
import type { AvailableLocale, Translation } from '@xleddyl/types'
import { ref, unref } from 'vue'

type Props = {
   label?: string
   error?: string
   grow?: boolean
   disabled?: boolean
   maxLength?: number
   locales?: AvailableLocale[]
   mode: 'text' | 'textarea' | 'rich-text'
}

const props = withDefaults(defineProps<Props>(), {
   locales: ['de', 'it', 'en'] as const,
})
const model = defineModel<Translation>()

const { locale } = useI18n()

const selectorRef = ref<HTMLDivElement | null>(null)
// Use unref to get the string value, avoiding reactive binding to global locale
const initialLocale = (unref(locale) as AvailableLocale) || props.locales?.[0]
const selectedLocale = ref<AvailableLocale>(initialLocale)

const updateLocale = (locale: AvailableLocale, value?: string) => {
   const current = model.value || {}

   if (value && value.trim() === '') {
      const { [locale]: _, ...rest } = current
      model.value = rest
   } else {
      model.value = { ...current, [locale]: value }
   }
}
</script>
